/**
 * VotingResultsView — per-item results bars + outcomes + turnout summary.
 * Shows closed/open item tallies; pending items are listed as not yet voted.
 */

'use client';

/**
 * USER DOCS: changes to this component should be accompanied by a
 * corresponding update to the user guide:
 * next_portal/user-docs/content/docs/total-operations/workflows/digital-voting/quorum-and-results.mdx
 * (published at user-docs.totalaccess.co.za/total-operations/workflows/digital-voting/quorum-and-results)
 */

import React, { useMemo, useState } from 'react';
import { Archive, Copy, FileDown, FileSpreadsheet, Loader2, MonitorPlay, ScrollText, Send, Trophy } from 'lucide-react';
import type { VotingItem, VotingSession } from './votingApi';
import { archiveSessionArtifacts, downloadSessionAudit, downloadSessionExport } from './votingApi';
import { Badge, ITEM_TYPE_LABELS, OutcomeBadge } from './shared';
import { notify } from '@/lib/notifications';
import UserDocsLink from '@/components/shared/UserDocsLink';
import WorkItemPreviewModal from '@/components/work-items/WorkItemPreviewModal';
import type { WorkItem } from '@/components/UniversalWorkItems';
import type { DocumentType } from '@/lib/reports';

const MINUTES_DOC_TYPES: Array<{ docType: DocumentType; label: string }> = [
  { docType: 'voting_minutes', label: 'Minutes' },
  { docType: 'voting_results', label: 'Results' },
  { docType: 'voting_attendance_register', label: 'Attendance' },
];

interface Props {
  session: VotingSession;
  items: VotingItem[];
  quorum: { eligible: number; present: number; requiredPct: number | null; met: boolean | null } | null;
  projectorUrl?: string;
}

const BAR_COLORS = ['#2563eb', '#0ea5e9', '#f59e0b', '#10b981', '#ef4444', '#6366f1', '#ec4899'];

export default function VotingResultsView({ session, items, quorum, projectorUrl }: Props) {
  const sorted = useMemo(() => [...items].sort((a, b) => a.seq - b.seq), [items]);
  const decided = sorted.filter((i) => i.status === 'closed');
  const eligible = quorum?.eligible ?? 0;

  const totals = useMemo(() => {
    const cast = decided.reduce((sum, i) => sum + (i.tally?.total ?? 0), 0);
    return {
      passed: decided.filter((i) => i.outcome === 'passed').length,
      failed: decided.filter((i) => i.outcome === 'failed').length,
      other: decided.filter((i) => i.outcome === 'tied' || i.outcome === 'no_quorum').length,
      cast,
    };
  }, [decided]);

  const [exporting, setExporting] = useState<'pdf' | 'csv' | 'audit' | 'archive' | null>(null);
  // Minutes/results/attendance preview — WorkItemPreviewModal renders the
  // server-generated document (unified report engine) with print/PDF/email.
  const [previewDoc, setPreviewDoc] = useState<DocumentType | null>(null);
  const [previewMenuOpen, setPreviewMenuOpen] = useState(false);

  const sessionWorkItem = useMemo<WorkItem>(() => ({
    id: session.id,
    item_number: session.item_number ?? undefined,
    item_type: 'voting',
    item_subtype: session.session_type,
    module_context: 'digital-voting',
    title: session.title,
    description: session.description,
    status: session.status,
    scheduled_start: session.scheduled_start,
    scheduled_end: session.voting_closes_at,
  }), [session]);

  const exportSession = async (format: 'pdf' | 'csv' | 'audit' | 'archive') => {
    if (exporting) return;
    setExporting(format);
    try {
      if (format === 'audit') {
        await downloadSessionAudit(session.id);
      } else if (format === 'archive') {
        const files = await archiveSessionArtifacts(session.id);
        const first = Object.values(files)[0];
        notify.success(`Archived to Operations Drive → ${first?.path?.replace(/\/[^/]+$/, '') ?? '/Voting'}`);
      } else {
        await downloadSessionExport(session.id, format);
      }
    } catch (err) {
      notify.error(err instanceof Error ? err.message : 'Export failed');
    } finally {
      setExporting(null);
    }
  };

  if (sorted.length === 0) {
    return (
      <div className="flex h-full flex-col items-center justify-center gap-2 text-center">
        <Trophy className="h-10 w-10 text-gray-200" />
        <p className="text-sm text-gray-400">No items on the ballot yet.</p>
      </div>
    );
  }

  return (
    <div className="h-full overflow-y-auto p-4">
      {/* Export actions */}
      <div className="mb-3 flex items-center justify-end gap-2">
        <UserDocsLink docsPath="/total-operations/workflows/digital-voting/quorum-and-results#the-results-tab" label="User guide: Quorum and results" className="mr-auto" />
        {projectorUrl && (
          <button
            onClick={() => {
              const url = `${window.location.origin}${projectorUrl}`;
              window.open(url, '_blank', 'noopener');
              void navigator.clipboard?.writeText(url).then(() => notify.success('Projector link copied'));
            }}
            className="flex items-center gap-1.5 rounded-lg border border-gray-200 bg-white px-3 py-1.5 text-xs font-medium text-gray-700 hover:bg-gray-50"
          >
            <MonitorPlay className="h-3.5 w-3.5 text-blue-600" />
            Projector view
            <Copy className="h-3 w-3 text-gray-400" />
          </button>
        )}
        <button
          onClick={() => void exportSession('pdf')}
          disabled={exporting !== null}
          className="flex items-center gap-1.5 rounded-lg border border-gray-200 bg-white px-3 py-1.5 text-xs font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50"
        >
          {exporting === 'pdf' ? (
            <Loader2 className="h-3.5 w-3.5 animate-spin" />
          ) : (
            <FileDown className="h-3.5 w-3.5 text-blue-600" />
          )}
          Minutes PDF
        </button>
        <button
          onClick={() => void exportSession('csv')}
          disabled={exporting !== null}
          className="flex items-center gap-1.5 rounded-lg border border-gray-200 bg-white px-3 py-1.5 text-xs font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50"
        >
          {exporting === 'csv' ? (
            <Loader2 className="h-3.5 w-3.5 animate-spin" />
          ) : (
            <FileSpreadsheet className="h-3.5 w-3.5 text-emerald-600" />
          )}
          Results CSV
        </button>
        <button
          onClick={() => void exportSession('audit')}
          disabled={exporting !== null}
          className="flex items-center gap-1.5 rounded-lg border border-gray-200 bg-white px-3 py-1.5 text-xs font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50"
        >
          {exporting === 'audit' ? (
            <Loader2 className="h-3.5 w-3.5 animate-spin" />
          ) : (
            <ScrollText className="h-3.5 w-3.5 text-amber-600" />
          )}
          Audit trail CSV
        </button>
        <div className="relative">
          <button
            onClick={() => setPreviewMenuOpen((v) => !v)}
            className="flex items-center gap-1.5 rounded-lg border border-gray-200 bg-white px-3 py-1.5 text-xs font-medium text-gray-700 hover:bg-gray-50"
            title="Preview, print, or email the official minutes/results/attendance to owners"
          >
            <Send className="h-3.5 w-3.5 text-violet-600" />
            Preview &amp; Email
          </button>
          {previewMenuOpen && (
            <div className="absolute right-0 z-20 mt-1 w-44 rounded-lg border border-gray-200 bg-white py-1 shadow-lg">
              {MINUTES_DOC_TYPES.map((opt) => (
                <button
                  key={opt.docType}
                  onClick={() => { setPreviewDoc(opt.docType); setPreviewMenuOpen(false); }}
                  className="block w-full px-3 py-1.5 text-left text-xs text-gray-700 hover:bg-gray-50"
                >
                  {opt.label}
                </button>
              ))}
            </div>
          )}
        </div>
        <button
          onClick={() => void exportSession('archive')}
          disabled={exporting !== null}
          className="flex items-center gap-1.5 rounded-lg border border-gray-200 bg-white px-3 py-1.5 text-xs font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50"
        >
          {exporting === 'archive' ? (
            <Loader2 className="h-3.5 w-3.5 animate-spin" />
          ) : (
            <Archive className="h-3.5 w-3.5 text-sky-600" />
          )}
          Archive to cloud
        </button>
      </div>

      {/* Summary strip */}
      <div className="mb-4 grid grid-cols-2 gap-2 sm:grid-cols-4">
        <div className="rounded-lg border border-gray-200 bg-white p-3">
          <p className="text-xs text-gray-500">Decided</p>
          <p className="text-lg font-semibold text-gray-900">
            {decided.length}<span className="text-sm font-normal text-gray-400">/{sorted.length}</span>
          </p>
        </div>
        <div className="rounded-lg border border-gray-200 bg-white p-3">
          <p className="text-xs text-gray-500">Passed</p>
          <p className="text-lg font-semibold text-emerald-700">{totals.passed}</p>
        </div>
        <div className="rounded-lg border border-gray-200 bg-white p-3">
          <p className="text-xs text-gray-500">Failed</p>
          <p className="text-lg font-semibold text-red-600">{totals.failed}</p>
        </div>
        <div className="rounded-lg border border-gray-200 bg-white p-3">
          <p className="text-xs text-gray-500">{session.delivery_mode === 'async' ? 'Turnout' : 'Present'}</p>
          <p className="text-lg font-semibold text-gray-900">
            {quorum?.present ?? 0}
            {eligible > 0 && <span className="text-sm font-normal text-gray-400">/{eligible}</span>}
          </p>
        </div>
      </div>

      <div className="space-y-3">
        {sorted.map((item) => {
          const tally = item.tally ?? {};
          const nonAbstain = item.options.reduce((sum, o) => sum + (tally[o.key] ?? 0), 0);
          const abstain = tally.abstain ?? 0;
          const cast = tally.total ?? nonAbstain + abstain;
          return (
            <div key={item.id} className="rounded-xl border border-gray-200 bg-white p-4">
              <div className="mb-2 flex flex-wrap items-center gap-2">
                <span className="text-xs font-semibold text-gray-400">#{item.seq}</span>
                <span className="text-sm font-medium text-gray-900">{item.title}</span>
                <Badge
                  label={ITEM_TYPE_LABELS[item.item_type] ?? item.item_type}
                  color="#4b5563"
                  bg="#f3f4f6"
                />
                <OutcomeBadge outcome={item.outcome} />
                <span className="ml-auto text-xs text-gray-400">
                  {item.status === 'closed' ? `${cast} cast` : item.status}
                </span>
              </div>

              {item.status === 'pending' || item.status === 'showing' ? (
                <p className="text-xs text-gray-400">Not yet voted.</p>
              ) : (
                <div className="space-y-1.5">
                  {item.options.map((opt, i) => {
                    const count = tally[opt.key] ?? 0;
                    const pct = nonAbstain > 0 ? (count / nonAbstain) * 100 : 0;
                    return (
                      <div key={opt.key} className="flex items-center gap-2">
                        <span className="w-6 shrink-0 text-center font-mono text-xs font-semibold text-gray-500">
                          {opt.key}
                        </span>
                        <div className="h-5 min-w-0 flex-1 overflow-hidden rounded bg-gray-100">
                          <div
                            className="flex h-full items-center rounded px-2 transition-all"
                            style={{
                              width: `${Math.max(pct, count > 0 ? 8 : 0)}%`,
                              backgroundColor: BAR_COLORS[i % BAR_COLORS.length],
                            }}
                          >
                            {pct > 15 && (
                              <span className="truncate text-xs font-medium text-white">
                                {opt.label}
                              </span>
                            )}
                          </div>
                        </div>
                        <span className="w-20 shrink-0 text-right text-xs text-gray-600">
                          {count} · {pct.toFixed(1)}%
                        </span>
                      </div>
                    );
                  })}
                  {abstain > 0 && (
                    <p className="text-xs text-gray-400">
                      {abstain} abstention{abstain === 1 ? '' : 's'} (excluded from the pass
                      denominator)
                    </p>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {previewDoc && (
        <WorkItemPreviewModal
          workItem={sessionWorkItem}
          isOpen
          onClose={() => setPreviewDoc(null)}
          forcedDocumentType={previewDoc}
        />
      )}
    </div>
  );
}
